import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Navigate } from 'react-router';
import { useTitle } from 'react-use';

import Loading from '@/components/atoms/Loading/Loading';
import NotFound from '@/components/pages/NotFound/NotFound';
import { useAccount } from '@/hooks/useAccount';
import {
  selectShouldShowOnboarding,
  selectShouldShowWelcome,
  selectUserId,
} from '@/redux/user/selectors';

/**
 * Loads the accounts and redirects to the first one.
 */
const Accounts = () => {
  const { t } = useTranslation();
  useTitle(t('pages.accounts'));
  const userId = useSelector(selectUserId);
  const needsWelcomePage = useSelector(selectShouldShowWelcome);
  const needsOnboarding = useSelector(selectShouldShowOnboarding);
  const { accounts, accountsStatus, lastUsedAccountSlug } = useAccount();

  // We need the userId to be loaded before checking the lastUsedAccountSlug
  if (!userId || !accounts || accountsStatus === 'pending') {
    return <Loading />;
  }

  // Check if user needs welcome page (both invited and new users)
  // Show Welcome page first for profile setup, then route appropriately
  if (needsWelcomePage) {
    return <Navigate to="/welcome" replace />;
  }

  if (needsOnboarding) {
    return <Navigate to="/onboarding" replace />;
  }

  if (accounts && accounts.length > 0) {
    // by default use the first account as a fallback
    let validSlug = accounts[0]?.slug;

    // if the last used account slug is still in the accounts list, use it
    if (lastUsedAccountSlug && accounts.some((acc) => acc.slug === lastUsedAccountSlug)) {
      validSlug = lastUsedAccountSlug;
    }

    return <Navigate to={`/${validSlug}/`} replace />;
  }

  // Re-direct user to create their first account if there are no accounts
  if (accounts.length === 0) {
    return <Navigate to="/create-account" replace />;
  }

  return <NotFound />;
};

export default Accounts;
